import { AxiosError } from 'axios';
import api from '../api/apiClient';
import type { ServiceItem } from '../types/service';
import {
  ServiceTemplateResponse,
  ServiceTemplates
} from '../types/serviceTemplate';
import type { StylistServicePayload } from '../types/stylist';

/**
 * Fetch all service templates.
 */
export async function getAllTemplates(): Promise<ServiceTemplates> {
  try {
    const response = await api.getWithParams<{ data: ServiceTemplates }>(
      '/service-templates'
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Get all templates error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Fetch templates for a specific category.
 * @param category Category name
 */
export async function getTemplatesByCategory(
  category: string
): Promise<ServiceItem[]> {
  try {
    const response = await api.getWithParams<{ data: ServiceItem[] }>(
      `/service-templates/categories/${category}`
    );
    return response.data.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error(
      'Get templates by category error:',
      err.response?.data || err.message
    );
    throw err;
  }
}

/**
 * Fetch a service ID by its subcategory name.
 * @param subcategory Subcategory identifier
 */
export async function getServiceBySubcategory(
  subcategory: string
): Promise<string> {
  try {
    const response = await api.getWithParams<{ data: { _id: string } }>(
      `/services/by-subcategory/${subcategory}`
    );
    return response.data.data._id;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error(
      'Get service by subcategory error:',
      err.response?.data || err.message
    );
    throw err;
  }
}

/**
 * Use a service template to create or update a service.
 * @param payload Stylist service payload including template ID
 */
export async function useTemplate(
  payload: StylistServicePayload
): Promise<{ success: boolean; data: ServiceTemplateResponse }> {
  try {
    console.log('Using template with payload:', payload);
    // Ensure all required fields are present
    if (!payload.service) {
      throw new Error('Service ID is required');
    }
    if (!payload.stylist && !payload.service) {
      throw new Error('Either stylist ID or service ID is required');
    }
    
    const response = await api.postWithData<{ success: boolean; data: ServiceTemplateResponse }>(
      `/service-templates/use/${payload.service}`,
      payload
    );
    // Backend returns { success: true, data: stylistService }
    return response.data;
  } catch (error) {
    const err = error as AxiosError<any>;
    console.error('Use template error:', err.response?.data || err.message);
    throw err;
  }
}

/**
 * Calculate price based on template and variation.
 */
export function getServicePrice(
  service: ServiceItem,
  length: string,
  thickness: string
): number {
  // Default implementation: returns base min
  return service.basePrice.min;
}

/**
 * Calculate duration based on template and variation.
 */
export function getServiceDuration(
  service: ServiceItem,
  length: string,
  thickness: string
): number {
  // Default implementation: returns base min
  return service.baseDuration.min;
}

/**
 * Group a flat list of services by category.
 */
export function groupServicesByCategory(
  services: ServiceItem[]
): Record<string, ServiceItem[]> {
  return services.reduce((acc, service) => {
    const { category } = service;
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {} as Record<string, ServiceItem[]>);
}

const serviceTemplateService = {
  getAllTemplates,
  getTemplatesByCategory,
  getServiceBySubcategory,
  useTemplate,
  getServicePrice,
  getServiceDuration,
  groupServicesByCategory,
};

export default serviceTemplateService;
